import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../ui/Input';
import { PRODUCT_CATEGORIES } from '../../constants/productCategories';
import { PAYMENT_SLIP_POLICY_OPTIONS } from '../../constants/paymentSlipPolicies';

const ProductFormPanel = ({ open, onClose, product, branches, onSave, saving }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Haircare');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('0');
  const [lowStockThreshold, setLowStockThreshold] = useState('5');
  const [branchId, setBranchId] = useState('');
  const [paymentSlipPolicy, setPaymentSlipPolicy] = useState('OPTIONAL');
  const [isPublished, setIsPublished] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    if (!open) return;
    if (product) {
      setName(product.name);
      setDescription(product.description || '');
      setCategory(product.category);
      setPrice(String(product.price));
      setStock(String(product.stock));
      setLowStockThreshold(String(product.lowStockThreshold ?? 5));
      setBranchId(product.branchId || '');
      setPaymentSlipPolicy(product.paymentSlipPolicy || 'OPTIONAL');
      setIsPublished(product.isPublished);
      setPreviews((product.images || []).map((i) => i.url));
    } else {
      setName('');
      setDescription('');
      setCategory('Haircare');
      setPrice('');
      setStock('0');
      setLowStockThreshold('5');
      setBranchId('');
      setPaymentSlipPolicy('OPTIONAL');
      setIsPublished(false);
      setPreviews([]);
    }
    setFiles([]);
    setUploadProgress({});
  }, [open, product]);

  const addFiles = (incoming) => {
    const list = [...incoming].slice(0, 5 - previews.length - files.length);
    setFiles((prev) => [...prev, ...list]);
    setPreviews((prev) => [...prev, ...list.map((f) => URL.createObjectURL(f))]);
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-lg flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 p-5">
          <h2 className="text-lg font-semibold">
            {product ? t('salon.products.editProduct') : t('salon.products.addProduct')}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave({
              name,
              description,
              category,
              price,
              stock,
              lowStockThreshold,
              branchId: branchId || null,
              paymentSlipPolicy,
              isPublished,
              files,
              onProgress: setUploadProgress,
            });
          }}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            <div
              className="rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-700 p-6 text-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                addFiles(e.dataTransfer.files);
              }}
            >
              <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('salon.products.dragDropImages')}</p>
              <label className="mt-2 inline-block cursor-pointer text-sm text-glow-400">
                {t('common.browse')}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-2 justify-center">
                {previews.map((url, i) => (
                  <img key={i} src={url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                ))}
              </div>
              {Object.keys(uploadProgress).length > 0 && (
                <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-500">{t('salon.products.uploading')}</p>
              )}
            </div>
            <Input label={t('salon.products.nameRequired')} value={name} onChange={(e) => setName(e.target.value)} required />
            <div>
              <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('common.description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('common.category')}</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('payments.slipPolicyLabel')}</label>
              <select
                value={paymentSlipPolicy}
                onChange={(e) => setPaymentSlipPolicy(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              >
                {PAYMENT_SLIP_POLICY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('salon.products.branchOptional')}</label>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
              >
                <option value="">{t('salon.products.allBranches')}</option>
                {(branches || []).map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label={t('salon.products.price')} type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} required />
              <Input label={t('salon.products.stock')} type="number" min={0} value={stock} onChange={(e) => setStock(e.target.value)} />
            </div>
            <Input label={t('salon.products.lowStockThreshold')} type="number" min={0} value={lowStockThreshold} onChange={(e) => setLowStockThreshold(e.target.value)} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
              {t('salon.products.publishImmediately')}
            </label>
          </div>
          <div className="border-t border-zinc-200 dark:border-zinc-800 p-5">
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving
                ? t('common.saving')
                : product
                  ? t('salon.products.updateProduct')
                  : t('salon.products.createProduct')}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
};

export default ProductFormPanel;
